import { useEffect, useState } from 'react';
import { apiUpload } from '../../data/api';
import { marginSource, money, isManual } from '../../data/hotels';

const srcStyle = {
  hotel:  { background:'rgba(169,118,26,0.16)', color:'#a9761a' },
  dest:   { background:'rgba(14,138,156,0.14)', color:'#0c6d7c' },
  agency: { background:'rgba(90,114,111,0.14)', color:'#5a726f' },
  fixed:  { background:'rgba(26,122,72,0.14)', color:'#1a7a48' },
};

/** Side panel for one hotel: what we sell it as, and what it costs. Also the "add a hotel" form. */
export default function HotelPanel({ hotel, destinations, isNew = false, onClose, onSave, onDelete }) {
  const [v, setV] = useState(hotel);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setV(hotel); }, [hotel]);

  const set = (k, val) => setV(prev => ({ ...prev, [k]: val }));
  const destination = destinations.find(d => d.id === v.destinationId);
  const src = marginSource(v, destination);
  // A hand-added hotel has no feed behind it, so its "supplier" fields are ours to type in.
  const manual = isNew || isManual(v);
  const fixed = v.priceOverride != null && v.priceOverride !== '';

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const { fileId, url } = await apiUpload(file);
      setV(prev => ({ ...prev, imageFileId: fileId, imageUrl: url }));
    } catch (err) { setError(err.message || 'Upload failed.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const save = async () => {
    if (saving) return;
    if (manual && !(v.name || '').trim()) { setError('A hotel needs a name.'); return; }
    setSaving(true); setError(null);
    try { await onSave(v); onClose(); }
    catch (err) { setError(err.message || 'Could not save.'); setSaving(false); }
  };

  const remove = async () => {
    if (deleting) return;
    if (!window.confirm(`Delete ${v.displayName || v.name}? This cannot be undone.`)) return;
    setDeleting(true); setError(null);
    try { await onDelete(v); onClose(); }
    catch (err) { setError(err.message || 'Could not delete.'); setDeleting(false); }
  };

  const subtitle = [v.cityName, v.regionName, v.countryName, v.starCategory,
    isManual(v) && !isNew ? 'added by hand' : v.supplierHotelId != null ? `supplier #${v.supplierHotelId}` : null]
    .filter(Boolean).join(' · ');

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:120, display:'flex', justifyContent:'flex-end', background:'rgba(6,40,45,0.4)', backdropFilter:'blur(5px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:470, height:'100%', overflowY:'auto', padding:28, background:'rgba(255,255,255,0.94)', backdropFilter:'blur(28px) saturate(180%)', borderLeft:'1px solid rgba(255,255,255,0.9)', boxShadow:'-24px 0 60px rgba(6,40,45,0.28)' }}>

        <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:6 }}>
          <div className="serif" style={{ fontSize:24, lineHeight:1.15, flex:1 }}>
            {isNew ? 'Add a hotel' : (v.displayName || v.name)}
          </div>
          <span onClick={onClose} style={{ cursor:'pointer', fontSize:22, color:'#5a726f', lineHeight:1 }}>×</span>
        </div>
        <div style={{ fontSize:12.5, color:'#5a726f', marginBottom:20 }}>
          {isNew ? 'For a hotel the supplier feed does not carry. You set its price yourself.' : subtitle}
        </div>

        {/* What the supplier charges vs what the site shows */}
        {!isNew && (
          <div className="glass" style={{ padding:16, borderRadius:16, marginBottom:18 }}>
            {fixed ? (
              <>
                <div style={{ display:'flex', alignItems:'baseline', gap:9 }}>
                  {v.supplierFrom != null && <>
                    <span style={{ color:'#5a726f', fontSize:14 }}>{money(v.supplierFrom)}</span>
                    <span style={{ color:'rgba(12,43,47,0.3)' }}>→</span>
                  </>}
                  <span style={{ fontWeight:800, fontSize:21 }}>{money(v.priceOverride)}</span>
                  <span style={{ marginLeft:'auto', ...srcStyle.fixed, padding:'3px 8px', borderRadius:6, fontSize:10, fontWeight:800, letterSpacing:'.06em', textTransform:'uppercase' }}>Fixed price</span>
                </div>
                <div style={{ fontSize:12, color:'#5a726f', marginTop:6 }}>
                  You set this price by hand — the margin is not applied.
                </div>
              </>
            ) : v.supplierFrom != null ? (
              <>
                <div style={{ display:'flex', alignItems:'baseline', gap:9 }}>
                  <span style={{ color:'#5a726f', fontSize:14 }}>{money(v.supplierFrom)}</span>
                  <span style={{ color:'rgba(12,43,47,0.3)' }}>→</span>
                  <span style={{ fontWeight:800, fontSize:21 }}>{money(v.sellFrom)}</span>
                  <span style={{ marginLeft:'auto', ...srcStyle[src.kind], padding:'3px 8px', borderRadius:6, fontSize:10, fontWeight:800, letterSpacing:'.06em', textTransform:'uppercase' }}>{src.label}</span>
                </div>
                <div style={{ fontSize:12, color:'#5a726f', marginTop:6 }}>
                  cheapest of {v.rateCount} supplier prices, {v.appliedMarginPercent}% added
                </div>
              </>
            ) : (
              <div style={{ fontSize:13.5, color:'#5a726f' }}>
                {manual
                  ? 'This hotel has no supplier prices — set a price below to sell it.'
                  : "The supplier sends no prices for this hotel, so it can't be sold until you set one below."}
              </div>
            )}
          </div>
        )}

        <div style={{ display:'grid', gap:14 }}>
          <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'12px 14px', borderRadius:13, background:'rgba(255,255,255,0.7)', border:'1px solid #f2e2d2' }}>
            <span style={{ fontWeight:700, fontSize:14 }}>Show on the site</span>
            <span onClick={() => set('isVisible', !v.isVisible)} style={{ width:52, height:30, borderRadius:100, padding:3, cursor:'pointer', display:'flex', flexShrink:0, background: v.isVisible ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : '#d7e6e2', justifyContent: v.isVisible ? 'flex-end' : 'flex-start' }}>
              <span style={{ width:24, height:24, borderRadius:'50%', background:'#fff', boxShadow:'0 2px 6px rgba(6,40,45,0.3)' }}></span>
            </span>
          </label>

          {/* Typed in by hand, so the fields the sync would own are editable here. */}
          {manual && (
            <>
              <label style={{ display:'block' }}>
                <span className="a-label">Hotel name</span>
                <input value={v.name ?? ''} onChange={e => set('name', e.target.value)} className="a-input" placeholder="Casa del Mar Boutique" />
              </label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <label style={{ display:'block' }}>
                  <span className="a-label">City</span>
                  <input value={v.cityName ?? ''} onChange={e => set('cityName', e.target.value)} className="a-input" placeholder="Punta Cana" />
                </label>
                <label style={{ display:'block' }}>
                  <span className="a-label">Country</span>
                  <input value={v.countryName ?? ''} onChange={e => set('countryName', e.target.value)} className="a-input" placeholder="Dominican Republic" />
                </label>
              </div>
              <label style={{ display:'block' }}>
                <span className="a-label">Rating</span>
                <input value={v.starCategory ?? ''} onChange={e => set('starCategory', e.target.value)} className="a-input" placeholder="5*" />
              </label>
            </>
          )}

          <label style={{ display:'block' }}>
            <span className="a-label">Destination</span>
            <select value={v.destinationId ?? ''} onChange={e => set('destinationId', e.target.value ? Number(e.target.value) : null)} className="a-input">
              <option value="">Not assigned</option>
              {destinations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>

          <label style={{ display:'block' }}>
            <span className="a-label">Price on the site</span>
            <input type="number" min="0" step="1" value={v.priceOverride ?? ''}
              placeholder={manual ? 'e.g. 275' : v.sellFrom != null ? `Calculated: ${money(v.sellFrom)} — leave blank to keep it` : 'No supplier price — set one to sell this hotel'}
              onChange={e => set('priceOverride', e.target.value === '' ? null : Number(e.target.value))} className="a-input" />
            <div style={{ fontSize:12, color:'#5a726f', marginTop:6 }}>
              {manual
                ? 'What customers pay. Nothing else prices this hotel, so without it the hotel cannot go on the site.'
                : 'A price here is what customers pay. It replaces the supplier price and the margin, and the per-room breakdown is hidden while it is set.'}
            </div>
          </label>

          {!manual && (
            <label style={{ display:'block', opacity: fixed ? 0.55 : 1 }}>
              <span className="a-label">Margin</span>
              <input type="number" min="0" max="500" step="1" value={v.marginPercent ?? ''} placeholder={`Using ${src.label.toLowerCase()} — leave blank to keep it`}
                onChange={e => set('marginPercent', e.target.value === '' ? null : Number(e.target.value))} className="a-input" />
              <div style={{ fontSize:12, color:'#5a726f', marginTop:6 }}>
                {fixed
                  ? 'Not used while a price is set above. Clear the price to go back to the margin.'
                  : `Blank follows ${destination ? `${destination.name}, then the agency default` : 'the agency default'}.`}
              </div>
            </label>
          )}

          <label style={{ display:'block' }}>
            <span className="a-label">Name shown on the site</span>
            <input value={v.displayName ?? ''} placeholder={v.name || 'Same as the hotel name'} onChange={e => set('displayName', e.target.value)} className="a-input" />
          </label>

          <label style={{ display:'block' }}>
            <span className="a-label">Description</span>
            <textarea rows={4} value={v.blurb ?? ''} onChange={e => set('blurb', e.target.value)} className="a-input" style={{ resize:'vertical' }}
              placeholder={manual ? 'What makes this hotel worth booking.' : 'The supplier sends no description — write one here.'} />
          </label>

          <div>
            <span className="a-label">Photo</span>
            {v.imageUrl
              ? <div style={{ height:150, borderRadius:14, marginBottom:8, backgroundColor:'rgba(14,138,122,0.06)', backgroundImage:`url(${v.imageUrl})`, backgroundSize:'cover', backgroundPosition:'center' }}></div>
              : <div style={{ height:150, borderRadius:14, marginBottom:8, border:'1px dashed rgba(14,138,122,0.4)', background:'rgba(255,255,255,0.55)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#5a726f' }}>{manual ? 'No photo yet' : 'The supplier sends no photos'}</div>}
            <label className="a-ghost" style={{ display:'block', textAlign:'center', padding:10, borderRadius:12, fontSize:13.5, cursor:'pointer' }}>
              {uploading ? 'Uploading…' : v.imageUrl ? 'Replace photo' : 'Upload a photo'}
              <input type="file" accept="image/*" onChange={onImage} style={{ display:'none' }} />
            </label>
          </div>
        </div>

        {error && <div style={{ marginTop:14, fontSize:13, fontWeight:700, color:'#c0492f' }}>{error}</div>}

        <div style={{ display:'flex', gap:10, marginTop:22 }}>
          <span onClick={onClose} className="a-ghost" style={{ flex:1, textAlign:'center', padding:13, borderRadius:13, fontSize:14, cursor:'pointer' }}>Cancel</span>
          <button onClick={save} disabled={saving} className="a-primary" style={{ flex:1, padding:13, borderRadius:13, fontSize:14, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : isNew ? 'Add hotel' : 'Save'}
          </button>
        </div>

        {/* Only a hand-added hotel can go: a supplier one would return on the next sync. */}
        {!isNew && isManual(v) && onDelete && (
          <div onClick={remove} style={{ marginTop:16, textAlign:'center', fontSize:13, fontWeight:700, color:'#c0492f', cursor:'pointer', opacity: deleting ? 0.6 : 1 }}>
            {deleting ? 'Deleting…' : 'Delete this hotel'}
          </div>
        )}
      </div>
    </div>
  );
}
